import { Layout } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import type { Template } from "@/hooks/useTemplates"
import { localizeName, localizeDescription, localizeTextPrompt } from "@/lib/i18n-content"
import {
  parseTemplateParameters,
  resolveTemplate,
  extractDefaultValues,
} from "@/lib/template-parameters"

/** 썸네일 URL에 캐시 버스팅 파라미터 추가 */
function withCacheBusting(url: string | null, updatedAt: string): string | null {
  if (!url) return null
  const timestamp = new Date(updatedAt).getTime()
  return `${url}?v=${timestamp}`
}

/** 템플릿 프리뷰 모달 */
export function TemplatePreviewModal({
  template,
  isOpen,
  onClose,
  onUse,
}: {
  template: Template | null
  isOpen: boolean
  onClose: () => void
  onUse?: () => void
}) {
  const { t } = useTranslation()

  if (!template) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full h-full max-w-none max-h-none sm:max-w-4xl sm:max-h-[90vh] sm:h-auto p-0 overflow-y-auto overscroll-contain rounded-none sm:rounded-lg inset-0 sm:inset-auto sm:top-[50%] sm:left-[50%] translate-x-0 translate-y-0 sm:translate-x-[-50%] sm:translate-y-[-50%]">
        <DialogTitle className="sr-only">{localizeName(template)}</DialogTitle>
        <div className="flex flex-col sm:flex-row min-h-0">
          {/* 이미지 영역 */}
          <div className="flex-shrink-0 sm:flex-1 bg-slate-100 flex items-center justify-center p-4">
            {template.thumbnailUrl ? (
              <img
                src={withCacheBusting(template.thumbnailUrl, template.updatedAt) ?? ''}
                alt={localizeName(template)}
                className="max-w-full object-contain rounded-lg shadow-lg"
              />
            ) : (
              <div className="w-32 h-32 flex items-center justify-center">
                <Layout className="w-16 h-16 text-slate-300" />
              </div>
            )}
          </div>

          {/* 정보 영역 */}
          <div className="flex-shrink-0 w-full sm:w-80 p-6 flex flex-col bg-white">
            <h2 className="text-xl font-semibold text-slate-900 mb-2 pr-8">
              {localizeName(template)}
            </h2>

            <p className="text-sm text-slate-600 mb-4">
              {localizeDescription(template)}
            </p>

            {/* 프롬프트 (현재 언어로 현지화 후, 파라미터는 기본값으로 치환하여 표시) */}
            {template.textPrompt && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg max-h-[300px] overflow-y-auto">
                <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {(() => {
                    const localizedPrompt = localizeTextPrompt(template)
                    return resolveTemplate(
                      localizedPrompt,
                      extractDefaultValues(parseTemplateParameters(localizedPrompt))
                    )
                  })()}
                </p>
              </div>
            )}

            {/* 비율 정보 */}
            {template.aspectRatio && (
              <div className="mb-4 text-sm text-slate-500">
                {t('templates.ratio', { ratio: template.aspectRatio })}
              </div>
            )}

            {/* 사용하기 버튼 (onUse가 있을 때만 표시) */}
            {onUse && (
              <button
                onClick={() => {
                  onUse()
                  onClose()
                }}
                className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                {t('templates.useThisTemplate')}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
